/**
 * Real API Client for QR Billing Customer Menu
 * Connects to backend routes: customerMenu.js and payment.js
 */

// Get backend URL from environment or default to localhost
// Remove /api suffix if present since we include it in the path
const getApiBaseUrl = () => {
  const url = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  // Remove /api suffix if present
  return url.replace(/\/api\/?$/, '');
};
const API_BASE_URL = getApiBaseUrl();

// Session token management
let sessionToken = null;

export function getSessionToken() {
  if (!sessionToken) {
    try {
      sessionToken = localStorage.getItem('qr.sessionToken');
      } catch (e) {
      console.warn('[API] localStorage not available');
    }
  }
  return sessionToken;
}

export function setSessionToken(token) {
  sessionToken = token;
  try {
    localStorage.setItem('qr.sessionToken', token);
    // ✅ FIX: Also store in sessionStorage as backup
    sessionStorage.setItem('qr.sessionToken', token);
  } catch (e) {
    console.warn('[API] localStorage not available:', e);
  }
}

// ✅ NEW: Clear session token (for logout or session reset)
export function clearSessionToken() {
  sessionToken = null;
  try {
    localStorage.removeItem('qr.sessionToken');
    sessionStorage.removeItem('qr.sessionToken');
  } catch (e) {
    console.warn('[API] Storage not available');
  }
}

// Core API request function
export async function apiRequest(method, path, { body, headers = {}, query } = {}) {
  const url = new URL(API_BASE_URL + path);
  
  // Add query parameters
  if (query) {
    Object.keys(query).forEach(key => {
      if (query[key] !== undefined && query[key] !== null) {
        url.searchParams.append(key, query[key]);
      }
    });
  }

  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers
    }
  };

  // Add session token if available
  const token = getSessionToken();
  if (token) {
    options.headers['x-session-token'] = token;
  }

  // Add body for POST/PUT/PATCH
  if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
    options.body = JSON.stringify(body);
  }


  try {
    const response = await fetch(url.toString(), options);
    const json = await response.json();

    // Store session token if provided
    if (json.sessionToken) {
      setSessionToken(json.sessionToken);
    }

    if (!response.ok) {
      throw new Error(json.error || json.message || `HTTP ${response.status}`);
    }

    return {
      status: response.status,
      json
    };
  } catch (error) {
    console.error(`[API ERROR] ${method} ${path}:`, error);
    throw error;
  }
}

// Convenience helpers
export const apiGet = (path, opts) => apiRequest('GET', path, opts);
export const apiPost = (path, body, opts = {}) => apiRequest('POST', path, { ...opts, body });
export const apiPut = (path, body, opts = {}) => apiRequest('PUT', path, { ...opts, body });
export const apiPatch = (path, body, opts = {}) => apiRequest('PATCH', path, { ...opts, body });
export const apiDelete = (path, opts) => apiRequest('DELETE', path, opts);

// ============================================================================
// CUSTOMER MENU API FUNCTIONS
// ============================================================================

/**
 * Get menu for a specific table
 * @param {number|string} businessId - Business ID
 * @param {string} tableNumber - Table number
 * @returns {Promise<Object>} Menu data with business info, table info, and categorized items
 */
export async function getCustomerMenu(businessId, tableNumber) {
  const { json } = await apiGet(`/api/customer-menu/${businessId}/${tableNumber}`);
  return json;
}

/**
 * Start or resume a table session
 * @param {Object} params - Session parameters
 * @param {number} params.businessId - Business ID
 * @param {string} params.tableNumber - Table number
 * @param {string} params.sessionToken - Session token (auto-generated if not provided)
 * @param {string} [params.customerName] - Customer name (optional)
 * @param {number} [params.partySize] - Number of people (optional)
 * @returns {Promise<Object>} Session data
 */
export async function startSession({ businessId, tableNumber, customerName, partySize }) {
  let token = getSessionToken();
  if (!token) {
    token = generateSessionToken();
    setSessionToken(token);
  }

  const { json } = await apiPost('/api/customer-menu/session/start', {
    businessId,
    tableNumber,
    sessionToken: token,
    customerName,
    partySize
  });
  return json;
}

/**
 * Place an order
 * @param {Object} params - Order parameters
 * @param {number} params.businessId - Business ID
 * @param {Array} params.items - Order items [{menu_item_id, quantity, customizations, special_requests}]
 * @param {string} [params.customerNotes] - Special notes (optional)
 * @param {number} [params.customerId] - Customer ID (optional, from authentication)
 * @returns {Promise<Object>} Order confirmation
 */
export async function placeOrder({ businessId, items, customerNotes, customerId }) {
  const token = getSessionToken();
  if (!token) {
    throw new Error('No active session. Please scan QR code again.');
  }

  const { json } = await apiPost('/api/customer-menu/order/place', {
    businessId,
    sessionToken: token,
    items,
    customerNotes,
    customerId
  });
  return json;
}

/**
 * Get order status for current session
 * @returns {Promise<Object>} Orders with status
 */
export async function getOrderStatus() {
  const token = getSessionToken();
  if (!token) {
    throw new Error('No active session');
  }

  const { json } = await apiGet(`/api/customer-menu/order/status/${token}`);
  return json;
}

/**
 * Call waiter for assistance
 * @param {number} businessId - Business ID
 * @returns {Promise<Object>} Confirmation with table number
 */
export async function callWaiter(businessId) {
  const token = getSessionToken();
  
  if (!businessId || !token) {
    throw new Error('No active session');
  }

  const { json } = await apiPost('/api/customer-menu/call-waiter', {
    businessId: parseInt(businessId),
    sessionToken: token
  });
  
  return json;
}

/**
 * End the current session
 * @returns {Promise<Object>} Confirmation
 */
export async function endSession() {
  const token = getSessionToken();
  if (!token) {
    throw new Error('No active session');
  }

  const { json } = await apiPost('/api/customer-menu/session/end', {
    sessionToken: token
  });
  
  // Clear session token
  sessionToken = null;
  try {
    localStorage.removeItem('qr.sessionToken');
  } catch (e) {}
  
  return json;
}

// ============================================================================
// PAYMENT API FUNCTIONS
// ============================================================================

/**
 * Create Stripe payment intent
 * @param {Object} params - Payment parameters
 * @param {string} [params.orderId] - Order ID (optional)
 * @param {number} params.amount - Amount to charge
 * @returns {Promise<Object>} Payment intent with client secret
 */
export async function createPaymentIntent({ orderId, amount }) {
  const token = getSessionToken();
  
  if (!token) {
    console.error('[API] No session token found in localStorage!');
    throw new Error('No active session. Please reload the page to start a new session.');
  }

  // Validate amount
  if (amount === undefined || amount === null) {
    throw new Error('Amount is required');
  }

  const numAmount = parseFloat(amount);
  if (isNaN(numAmount) || numAmount < 0) {
    throw new Error('Invalid amount');
  }

  // Backend expects sessionToken in body, not just headers
  const requestBody = {
    sessionToken: token,
    amount: numAmount
  };
  
  // Only include orderId if it's defined
  if (orderId !== undefined && orderId !== null) {
    requestBody.orderId = orderId;
  }


  try {
    const response = await fetch(API_BASE_URL + '/api/payment/create-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });
    
    const json = await response.json();
    
    if (!response.ok) {
      // Check if it's a session error
      if (json.code === 'SESSION_NOT_FOUND' || json.code === 'SESSION_NOT_ACTIVE') {
        console.error('[API] Session error detected:', json);
        
        // If action is to reload, offer that to the user
        if (json.action === 'RELOAD_PAGE') {
          clearSessionToken();
          const shouldReload = window.confirm(
            'Your session has expired. Click OK to reload the page and start a new session.'
          );
          if (shouldReload) {
            window.location.reload();
            throw new Error('Reloading page...');
          }
        }
        
        clearSessionToken();
        throw new Error(json.message || 'Session expired. Please reload the page.');
      }
      
      throw new Error(json.error || json.message || `HTTP ${response.status}`);
    }
    
    return json;
  } catch (error) {
    console.error('[API] Payment intent error:', error);
    
    // ✅ FIX: If session error, provide clear actionable message
    if (error.message && (
      error.message.includes('Invalid or expired session') ||
      error.message.includes('SESSION_NOT_ACTIVE') ||
      error.message.includes('SESSION_NOT_FOUND') ||
      error.message.includes('session')
    )) {
      console.error('[API] Session expired or invalid. Clearing local session.');
      clearSessionToken();
      throw new Error('Your session has expired. Please reload the page (F5) to start a new session.');
    }
    throw error;
  }
}

/**
 * Get payment status
 * @param {string} paymentIntentId - Stripe payment intent ID
 * @returns {Promise<Object>} Payment status
 */
export async function getPaymentStatus(paymentIntentId) {
  const { json } = await apiGet(`/api/payment/status/${paymentIntentId}`);
  return json;
}

// ============================================================================
// CUSTOMER AUTHENTICATION FUNCTIONS
// ============================================================================

/**
 * Get customer data from localStorage
 * @returns {Object|null} Customer object or null
 */
export function getCustomerData() {
  try {
    const data = localStorage.getItem('customer');
    return data ? JSON.parse(data) : null;
  } catch (e) {
    console.warn('Failed to get customer data:', e);
    return null;
  }
}

/**
 * Save customer data to localStorage
 * @param {Object} customer - Customer object
 */
export function setCustomerData(customer) {
  try {
    localStorage.setItem('customer', JSON.stringify(customer));
  } catch (e) {
    console.warn('Failed to save customer data:', e);
  }
}

/**
 * Clear customer data (logout)
 */
export function clearCustomerData() {
  try {
    localStorage.removeItem('customer');
  } catch (e) {
    console.warn('Failed to clear customer data:', e);
  }
}

/**
 * Get customer orders for specific business
 * @param {number} customerId - Customer ID
 * @param {number} businessId - Business ID
 * @returns {Promise<Object>} Order history
 */
export async function getCustomerOrders(customerId, businessId) {
  const { json } = await apiGet(`/api/customer-auth/orders/${customerId}/${businessId}`);
  return json;
}

/**
 * Clear table - End session and reset table to available
 * @param {string} tableNumber - Table number to clear
 * @returns {Promise<Object>} Confirmation
 */
export async function clearTable(tableNumber) {
  const token = localStorage.getItem('token');
  const { json } = await apiPost(`/api/waiter/table/${tableNumber}/clear`, {}, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  return json;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Generate a unique session token
 * @returns {string} UUID v4 session token
 */
function generateSessionToken() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

/**
 * Parse business ID and table number from URL hash
 * Supports both formats:
 * - New format: #b=10&t=T2
 * - Old format: #/menu/:businessId/:tableNumber
 * @returns {Object|null} { businessId, tableNumber } or null if not found
 */
export function parseQRParams() {
  const hash = window.location.hash;
  
  // Try new format first: #b=10&t=T2 or #b=10&t=OWNER&orderType=dining
  if (hash.includes('b=') && hash.includes('t=')) {
    const params = new URLSearchParams(hash.substring(1)); // Remove '#' and parse
    const businessId = params.get('b');
    const tableNumber = params.get('t');
    const orderType = params.get('orderType'); // Extract orderType if present
    
    if (businessId && tableNumber) {
      return {
        businessId: parseInt(businessId, 10),
        tableNumber: tableNumber,
        orderType: orderType || null // Include orderType (dining/takeaway)
      };
    }
  }
  
  // Fallback to old format: #/menu/10/T2
  const match = hash.match(/#\/menu\/(\d+)\/([^/?]+)/);
  
  if (match) {
    return {
      businessId: parseInt(match[1], 10),
      tableNumber: match[2],
      orderType: null
    };
  }
  
  return null;
}
